import importAll from "import-all.macro";

/* eslint-disable sort-keys */
import { theme } from "@chakra-ui/react";
import { createBreakpoints } from "@chakra-ui/theme-tools";
import { extendTheme } from "@chakra-ui/react";

import themeColors from "./constants/colors.json";
import fonts from "./constants/fonts.json";
import fontWeights from "./constants/fontWeights";
// import { measure } from "./constants/constants";
import sizes from "./constants/sizes";
import borders from "./constants/borders";
import textStyles from "./constants/textStyles";

const componentStyles = importAll.sync("./componentStyles/*.js");

const shadows = {
    // Used for things that appear over content but aren't popups/overlays
    dropdown: "2px 3px 10px 0 rgba(61, 68, 75, .1)",
    card: "0 0.75rem 1.5rem rgb(18 38 63 / 3%)",
    buttonHover: "0 1rem 2.5rem rgba(18, 38, 63, 0.1), 0 0.5rem 1rem -0.75rem rgba(18, 38, 63, 0.1)",
    workflowQuickLink: "2px 3px 10px 0 rgb(61 68 75 / 20%)",
    containerBox: "rgba(149, 157, 165, 0.2) 0px 8px 24px"
};
const transitions = {
    default: { duration: "1s" },
    jiggle: { duration: "0.1s" },
    short: { duration: "0.2s" }
};
const breakpoints = createBreakpoints({
    sm: "30em",
    md: "48em",
    lg: "62em",
    xl: "80em"
});

const letterSpacings = {
    stretch: "0.08em"
};

const mode = (darkMode, lightMode) => ({ colorMode }) => colorMode === "dark" ? darkMode : lightMode;
const globalStyles = ({ root }) => props => ({
    "html body #document #v3groupslist, html body #document #v3userlist": {
        backgroundColor: "transparent !important",
        padding: "0 !important",
        marginBottom: "1rem !important"
    },
    "html body [id^=\"v3\"] *": {
        boxSizing: "border-box"
    },
    [root]: {
        "fontFamily": "body",
        // "bg": mode("gray.600", "gray.300")(props),
        "h1, h2, h3, h4, h5, h6": {
            fontFamily: "heading",
            padding: 0,
            margin: 0
        },
        "html, body, div:not(.chakra-slide), header, nav, main, footer, select": {
            maxWidth: "none"
        },
        "div:not(.chakra-slide) [data - popper - escaped]": {
            maxWidth: "24rem" // fix popper overflow
        },
        "a": {
            color: "inherit",
        },
        "body,caption,th,td,input,textarea,select,option,legend,fieldset": {
            fontFamily: "body"
        },
        "body": {
            bg: mode("gray.600", "gray.300")(props),
        },
        "*, body": {
            fontFamily: "body",
            color: mode("gray.100", "gray.900")(props),
            lineHeight: "base",
            fontFeatureSettings: "\"pnum\"",
            fontVariantNumeric: "proportional-nums",
            // maxWidth: `${measure}ch`,
            _placeholder: {
                color: mode("gray.400", "whiteAlpha.400")(props)
            },
            _before: {
                borderColor: mode("gray.200", "whiteAlpha.300")(props),
                wordWrap: "break-word",
            },
            _after: {
                borderColor: mode("gray.200", "whiteAlpha.300")(props),
                wordWrap: "break-word",
            },
            borderColor: mode("gray.200", "whiteAlpha.300")(props),
            wordWrap: "break-word",
            transition: "background-color 0.2s",
            boxSizing: "border-box"
        },
        "*::placeholder": {
            color: mode("gray.400", "whiteAlpha.400")(props),
        },
        "*, *::before, &::after": {
            borderColor: mode("gray.200", "whiteAlpha.300")(props),
            wordWrap: "break-word",
            boxSizing: "border-box"
        },
    },
    ['div.chakra-slide']: {
        maxWidth: '420px',
    }
});

export default ({ root }) => extendTheme({
    ...theme,
    breakpoints,
    colors: {
        ...theme.colors,
        ...themeColors,
    },
    fonts: {
        body: `${fonts.ProximaNovaRegular.family}, sans-serif`,
        heading: `${fonts.ProximaNovaRegular.family}, system-ui, sans-serif`,
        // heading: `${fonts.SourceSansProRegular.family}, system-ui, sans-serif`, TODO: clarify if we need this font still
        mono: "Menlo, monospace",
    },
    letterSpacings: {
        ...theme.letterSpacings,
        ...letterSpacings
    },
    ...sizes,
    ...borders,
    ...fontWeights,
    shadows: {
        ...theme.shadows,
        ...shadows,
    },
    transitions: {
        ...transitions,
    },
    zIndices: {
        ...theme.zIndices
    },


    styles: {
        global: globalStyles({ root }),
        [`${root} h1`]: {
            variant: "text.heading",
            fontSize: 32,
            fontFamily: "heading",
            fontWeight: "heading",
            color: "primary",
            mt: 4,
            mb: 2,
        },
        [`${root}`]: {
            // uses the theme values provided above
            fontFamily: "body",
            fontWeight: "body",
        },
    },

    layerStyles: { // todo + move to own file
        base: {
            bg: "gray.50",
            border: "2px solid",
            borderColor: "gray.500",
        },

    },

    textStyles: {
        ...textStyles
    },

    components: { ...Object.assign({}, ...Object.values(componentStyles).map(i => i.default)) }
});
